import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import { baseUrl } from '../config';

const ProductionHouseSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    company_name: '',
    company_description: '',
    company_email: '',
    company_phone: '',
    company_website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    founded_year: '',
    company_size: 'Medium',
    industry_focus: 'Film',
    company_logo: null
  });

  const [step, setStep] = useState(1);
  const totalSteps = 3;

  const companySizes = ['Small', 'Medium', 'Large', 'Enterprise'];
  const industryFocuses = ['Film', 'TV', 'Documentary', 'Music Video', 'Commercial', 'All'];

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle logo upload
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10485760) {
      setError('Logo file is too large. Maximum size is 10MB.');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.');
      return;
    }

    setLogoFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload logo to backend
  const uploadLogo = async () => {
    if (!logoFile) return null;

    try {
      setUploadingLogo(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', logoFile);
      formDataUpload.append('file_type', 'image');
      formDataUpload.append('user_id', 0); // Will be set by backend

      const response = await fetch(`${baseUrl}/upload/direct`, {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        throw new Error('Logo upload failed');
      }

      const data = await response.json();
      return data.filename; // Return just the filename
    } catch (err) {
      setError('Failed to upload logo. Please try again.');
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Upload logo first if exists
      let logoFilename = null;
      if (logoFile) {
        logoFilename = await uploadLogo();
        if (!logoFilename) {
          setLoading(false);
          return; // Upload failed
        }
      }

      // Prepare data for backend
      const submitData = {
        ...formData,
        company_logo: logoFilename,
        founded_year: formData.founded_year ? parseInt(formData.founded_year) : null
      };

      // Remove empty optional fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === null) {
          delete submitData[key];
        }
      });

      // Send to backend
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${baseUrl}/createProductionHouse`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Setup failed');
      }

      // Success! Redirect to dashboard
      navigate('/production-house');
    } catch (err) {
      setError(err.message || 'Failed to complete setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Navigate steps
  const nextStep = () => {
    if (step === 1 && !formData.company_name.trim()) {
      setError('Company name is required to continue');
      return;
    }
    setError('');
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => {
    setError('');
    setStep(prev => Math.max(prev - 1, 1));
  };

  // Render step content
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold mb-4">Basic Information</h3>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <span>Company Name</span>{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="e.g., Stellar Productions"
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                required
              />
            </div>

            {/* Company Description */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Company Description
              </label>
              <textarea
                name="company_description"
                value={formData.company_description}
                onChange={handleChange}
                placeholder="Tell us about your production house..."
                rows="4"
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Company Logo
              </label>
              <div className="flex items-center space-x-4">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo Preview"
                    className="w-24 h-24 object-cover rounded-full border-2 border-gray-600"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center text-3xl font-bold border-2 border-gray-600">
                    {formData.company_name.charAt(0) || '?'}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="text-gray-400"
                />
              </div>
            </div>

            {/* Industry Focus */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Industry Focus
              </label>
              <select
                name="industry_focus"
                value={formData.industry_focus}
                onChange={handleChange}
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
              >
                {industryFocuses.map(focus => (
                  <option key={focus} value={focus}>{focus}</option>
                ))}
              </select>
            </div>

            {/* Company Size */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Company Size
              </label>
              <select
                name="company_size"
                value={formData.company_size}
                onChange={handleChange}
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
              >
                {companySizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>

            {/* Founded Year */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Founded Year
              </label>
              <input
                type="number"
                name="founded_year"
                value={formData.founded_year}
                onChange={handleChange}
                placeholder="e.g., 2020"
                min="1900"
                max={new Date().getFullYear()}
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold mb-4">Contact Information</h3>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Company Email
              </label>
              <input
                type="email"
                name="company_email"
                value={formData.company_email}
                onChange={handleChange}
                placeholder="contact@company.com"
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Company Phone
              </label>
              <input
                type="tel"
                name="company_phone"
                value={formData.company_phone}
                onChange={handleChange}
                placeholder="+1 (555) 123-4567"
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Company Website
              </label>
              <input
                type="url"
                name="company_website"
                value={formData.company_website}
                onChange={handleChange}
                placeholder="https://www.company.com"
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold mb-4">Location</h3>

            {/* Address Line 1 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Address Line 1
              </label>
              <input
                type="text"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleChange}
                placeholder="Street address"
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>

            {/* Address Line 2 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Address Line 2
              </label>
              <input
                type="text"
                name="address_line2"
                value={formData.address_line2}
                onChange={handleChange}
                placeholder="Apartment, suite, etc. (optional)"
                className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>

            {/* City & State */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  State/Province
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="State"
                  className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                />
              </div>
            </div>

            {/* Country & Postal Code */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Country"
                  className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Postal Code
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder="Postal Code"
                  className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO
        title="Production House Setup"
        description="Set up your production house profile, team structure, and project preferences on ProducerBuddy."
        url="https://producerbuddy.pilomation.com/production-house-setup"
        robots="noindex, nofollow"
      />
      <SubNav />
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-gray-800 rounded-lg shadow-2xl p-8" translate="no" suppressHydrationWarning={true}>
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Welcome to Producer Buddy!</h1>
            <p className="text-gray-400">
              Let's set up your Production House profile to get started
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              {[1, 2, 3].map(num => (
                <div
                  key={num}
                  className={`flex-1 text-center ${num <= step ? 'text-yellow-400' : 'text-gray-600'
                    }`}
                >
                  <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${num <= step ? 'bg-yellow-400 text-black' : 'bg-gray-700'
                    }`}>
                    {num}
                  </div>
                  <p className="text-xs mt-1">
                    {num === 1 ? 'Basic Info' : num === 2 ? 'Contact' : 'Location'}
                  </p>
                </div>
              ))}
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-500 rounded-lg">
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} translate="no" suppressHydrationWarning={true}>
            {renderStep()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                type="button"
                onClick={prevStep}
                disabled={step === 1}
                className={`px-6 py-3 rounded-lg font-semibold ${step === 1
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-700 text-white hover:bg-gray-600'
                  }`}
              >
                Previous
              </button>

              {step < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-3 bg-yellow-400 text-black rounded-lg font-semibold hover:bg-yellow-500"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || uploadingLogo}
                  className={`px-6 py-3 rounded-lg font-semibold ${loading || uploadingLogo
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                >
                  {loading || uploadingLogo ? 'Setting up...' : 'Complete Setup'}
                </button>
              )}
            </div>
          </form>

          {/* Skip Option */}
          <div className="text-center mt-4">
            <button
              onClick={() => navigate('/production-house')}
              className="text-gray-400 hover:text-white text-sm"
            >
              Skip for now (you can complete this later)
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductionHouseSetup;
